import React, { useEffect, useRef, useState } from "react";
// react component for creating dynamic tables
import { Redirect } from "react-router-dom";
import PropTypes from "prop-types";
import Datetime from "react-datetime";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";

// core components
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Button from "components/CustomButtons/Button.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardIcon from "components/Card/CardIcon.js";
import CardHeader from "components/Card/CardHeader.js";

import styles from "assets/jss/material-dashboard-pro-react/views/formsStyle.js";
import { MailOutline } from "@material-ui/icons";
import { FormControl, Input, InputLabel, TextField } from "@material-ui/core";
import MerchantProductDialog from "components/MerchantProductDialog/MerchantProductDialog";
import { Autocomplete } from "@material-ui/lab";
import { travelApi } from "functions/apis";
import CustomInput from "components/CustomInput/CustomInput";

const useStyles = makeStyles(styles);

export default function CreateMerchantOnlineProductCost(props) {
  const source = useRef(new AbortController());
  const [redirect, setRedirect] = useState(false);
  const [payload, setPayload] = useState({
    productID: "",
    variantID: "",
    effectiveDate: null,
    expiryDate: null,
    cost: 0
  });
  const [showProductModal, setShowProductModal] = React.useState(false);
  const [productData, setProductData] = React.useState({
    id: "",
    name: ""
  });
  const [variantList, setVariantList] = React.useState([]);
  const [disabled, setDisabled] = React.useState(false);

  useEffect(() => {
    return () => {
      source.current.abort();
      if (payload) {
        setRedirect(true);
      }
    };
  }, []);

  useEffect(() => {
    if (payload.productID && payload.productID != "") {
      travelApi
        .get(`products/${payload.productID}/variants`, {
          signal: source.current.signal,
          params: {
            filter: {
              searchString: ""
            },
            pagination: {
              pageSize: 0,
              pageIndex: 0
            }
          }
        })
        .then(data => {
          setVariantList(data.records);
        })
        .catch(error => console.log(error));
    }
  }, [payload]);

  const handleProductIDChanged = e => {
    if (e) {
      setProductData({ id: e.id, name: e.name });
      setPayload(prev => ({
        ...prev,
        productID: e.id
      }));
      setShowProductModal(false);
    }
  };

  const handleEffectiveDateChanged = e =>
    setPayload(prev => ({
      ...prev,
      effectiveDate: e === "" ? null : e
    }));

  const handleExpiryDateChanged = e =>
    setPayload(prev => ({
      ...prev,
      expiryDate: e === "" ? null : e
    }));

  const handleVoucherVariantChanged = (e, v) => {
    if (v === null) {
      setPayload(prev => ({
        ...prev,
        variantID: null
      }));
      return;
    }

    setPayload(prev => ({
      ...prev,
      variantID: v.id
    }));
  };

  const handleCostChanged = e =>
    setPayload({ ...payload, cost: e.target ? e.target.value : "" });

  const handleSubmit = e => {
    e.preventDefault();
    setDisabled(true);
    const body = { ...payload, cost: parseInt(payload.cost) };

    travelApi
      .post("/ProductCosts", body, {
        signal: source.current.signal
      })
      .then(() => setRedirect(true))
      .catch(err => {
        console.log(err);
        setDisabled(false);
      });
  };

  const classes = useStyles();

  return redirect ? (
    <Redirect to={"/admin/MerchantOnlineProductCost/List"} />
  ) : (
    <GridContainer>
      <GridItem xs={12}>
        <Card>
          <CardHeader color="rose" icon>
            <CardIcon color="rose">
              <MailOutline />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>
              CREATE MERCHANT ONLINE PRODUCT COST
            </h4>
          </CardHeader>
          <CardBody>
            <Button onClick={() => props.history.goBack()} value="Back">
              BACK
            </Button>
            <form onSubmit={handleSubmit}>
              <GridContainer>
                <GridItem xs={12} sm={12}>
                  <FormControl fullWidth className={classes.selectFormControl}>
                    <InputLabel
                      htmlFor="selAgent"
                      className={classes.selectLabel}
                    >
                      Merchant Product
                    </InputLabel>
                    <Input
                      onClick={() => setShowProductModal(prev => !prev)}
                      className={classes.select}
                      value={productData ? productData.name : ""}
                    />
                    <MerchantProductDialog
                      openModal={showProductModal}
                      setOpenModal={val => setShowProductModal(val)}
                      onSelect={handleProductIDChanged}
                    />
                  </FormControl>
                </GridItem>
                <GridItem xs={12} sm={12}>
                  <Autocomplete
                    id="voucherType"
                    onChange={handleVoucherVariantChanged}
                    options={variantList}
                    classes={{
                      option: classes.option
                    }}
                    autoHighlight
                    getOptionLabel={option => option.name}
                    renderOption={option => (
                      <React.Fragment>
                        <b>{`${option.name} (${option.ageRangeFrom} - ${option.ageRangeTo})`}</b>
                      </React.Fragment>
                    )}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label="Age Variant"
                        variant="outlined"
                        inputProps={{
                          ...params.inputProps
                        }}
                      />
                    )}
                  />
                </GridItem>
                <GridItem xs={12}>
                  <div style={{ height: 12 }}></div>
                </GridItem>
                <GridItem xs={12} sm={6} md={6}>
                  <FormControl fullWidth>
                    <Datetime
                      value={payload.effectiveDate ?? null}
                      onChange={handleEffectiveDateChanged}
                      timeFormat={true}
                      closeOnSelect={true}
                      utc
                      inputProps={{
                        placeholder: "Effective Date"
                      }}
                    />
                  </FormControl>
                </GridItem>
                <GridItem xs={12} sm={6} md={6}>
                  <FormControl fullWidth>
                    <Datetime
                      value={payload.expiryDate ?? null}
                      onChange={handleExpiryDateChanged}
                      timeFormat={true}
                      closeOnSelect={true}
                      utc
                      inputProps={{
                        placeholder: "Expiry Date"
                      }}
                    />
                  </FormControl>
                </GridItem>
                <GridItem xs={12} sm={6} md={6}>
                  <CustomInput
                    labelText="Cost"
                    formControlProps={{
                      fullWidth: true
                    }}
                    inputProps={{
                      onChange: handleCostChanged,
                      value: payload.price === null ? 0 : payload.price,
                      type: "number"
                    }}
                  />
                </GridItem>
              </GridContainer>
              <Button
                disabled={disabled}
                color="rose"
                type="submit"
                value="Submit"
              >
                Submit
              </Button>
            </form>
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}

CreateMerchantOnlineProductCost.propTypes = {
  history: PropTypes.object
};
